import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { CommandGroup, CommandItem } from "@loot-os/ui/components/command";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { matchesSearch } from "../lib/boxes";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";
import { PickerCombobox } from "./PickerCombobox";

interface PickableBox {
  id: string;
  name: string;
  slug: string;
  imageUrl: string;
  status: "draft" | "active" | "pre_release" | "archived";
}

/**
 * Picks one of the environment's boxes, its own or an enabled box template, for a link to its
 * storefront page; archived boxes are left out.
 */
export function NotificationBoxPicker({
  disabled,
  onPick,
}: {
  disabled?: boolean;
  onPick: (box: { id: string; name: string }) => void;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const search = useDebounced(query.trim());

  const own = useQuery({
    ...trpc.boxes.list.queryOptions({ search: search || undefined }),
    enabled: open,
    placeholderData: (previous) => previous,
  });
  // Small and unpaginated, so it is filtered in memory, as the Boxes page does.
  const templates = useQuery({ ...trpc.boxTemplates.list.queryOptions(), enabled: open });

  const ownBoxes: PickableBox[] = (own.data ?? []).filter((box) => box.status !== "archived");
  const templateBoxes: PickableBox[] = (templates.data ?? []).filter(
    (box) => box.enabled && box.status !== "archived" && matchesSearch(box, search),
  );
  const loading = own.isFetching || templates.isFetching;

  const row = (box: PickableBox) => (
    <CommandItem
      key={box.id}
      value={box.id}
      onSelect={() => {
        onPick({ id: box.id, name: box.name });
        setOpen(false);
        setQuery("");
      }}
    >
      <BoxImage src={box.imageUrl} className="size-8 shrink-0 rounded border object-contain" />
      <span className="truncate">{box.name}</span>
      {(box.status === "draft" || box.status === "pre_release") && (
        <Badge variant="outline" className="ml-auto">
          {box.status === "pre_release" ? "pre-release" : "draft"}
        </Badge>
      )}
    </CommandItem>
  );

  return (
    <PickerCombobox
      open={open}
      onOpenChange={setOpen}
      query={query}
      onQueryChange={setQuery}
      triggerLabel="Link to a box…"
      searchPlaceholder="Search name, slug or id"
      emptyMessage={loading ? "Searching…" : "No matching boxes."}
      disabled={disabled}
    >
      {ownBoxes.length > 0 && <CommandGroup heading="Your boxes">{ownBoxes.map(row)}</CommandGroup>}
      {templateBoxes.length > 0 && (
        <CommandGroup heading="Box templates">{templateBoxes.map(row)}</CommandGroup>
      )}
    </PickerCombobox>
  );
}
